import { apiFetch } from "../api.js";

export type Bureau =
  | "fico"
  | "equifax"
  | "experian"
  | "transunion"
  | "vantage_score";
export type FreezeStatus = "frozen" | "thawed" | "fraud_alert";

export interface CreditReport {
  household_id: string;
  user_id: string;
  bureau: Bureau;
  score: number;
  score_date: string;
  factors?: string[];
  inquiries_count?: number;
  accounts_open?: number;
  total_credit_limit_cents?: number;
  total_balance_cents?: number;
  utilization_pct?: number;
  freeze_status?: FreezeStatus;
  freeze_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface CreditReportsListResponse {
  credit_reports: CreditReport[];
  count: number;
}

export interface UtilizationResponse {
  by_bureau: Record<
    Bureau,
    { user_id: string; bureau: Bureau; score: number; score_date: string }
  >;
  utilization: {
    limit_cents: number;
    balance_cents: number;
    pct: number;
    account_count: number;
  };
}

export function listCreditReports(): Promise<CreditReportsListResponse> {
  return apiFetch<CreditReportsListResponse>("/v1/credit-reports");
}

export function getCreditReport(
  userId: string,
  scoreDate: string,
): Promise<CreditReport> {
  return apiFetch<CreditReport>(
    `/v1/credit-reports/${encodeURIComponent(userId)}/${encodeURIComponent(scoreDate)}`,
  );
}

export interface CreateCreditReportInput {
  user_id: string;
  bureau: Bureau;
  score: number;
  score_date: string;
  factors?: string[];
  inquiries_count?: number;
  accounts_open?: number;
  total_credit_limit_cents?: number;
  total_balance_cents?: number;
  utilization_pct?: number;
  freeze_status?: FreezeStatus;
  freeze_date?: string;
  notes?: string;
}

export function createCreditReport(
  input: CreateCreditReportInput,
): Promise<CreditReport> {
  return apiFetch<CreditReport>("/v1/credit-reports", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateCreditReport(
  userId: string,
  scoreDate: string,
  input: Partial<CreateCreditReportInput>,
): Promise<CreditReport> {
  return apiFetch<CreditReport>(
    `/v1/credit-reports/${encodeURIComponent(userId)}/${encodeURIComponent(scoreDate)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export function deleteCreditReport(
  userId: string,
  scoreDate: string,
): Promise<void> {
  return apiFetch<void>(
    `/v1/credit-reports/${encodeURIComponent(userId)}/${encodeURIComponent(scoreDate)}`,
    { method: "DELETE" },
  );
}

export function getCreditUtilization(): Promise<UtilizationResponse> {
  return apiFetch<UtilizationResponse>("/v1/credit-reports/utilization");
}
